import { Tag } from "antd";
import { useTranslation } from "react-i18next";
import { BatteryBase, Beacon, BeaconBase, SCBOneDevice } from "../api/models";
import "./Battery.css";
import { BeaconErrorStatus } from "./BeaconErrorStatus";
import { formatLocalizedDateTime } from "../util/formatting";

export function Battery(props: {
  environment: BatteryBase;
  iconOnly?: boolean;
  absolute?: boolean;
}) {
  const { t } = useTranslation();
  const { environment, absolute } = props;
  const voltage = environment?.v ?? 0;
  const percentage = environment?.percentLeft ?? 0;
  const roundedPercentage = Math.round(percentage * 100);

  const days = Math.floor(percentage / 24);

  const tooltip =
    days < 1
      ? t("beacon:battery-title-less-than-a-day", {
          voltage: voltage?.toFixed(2).toLocaleString(),
        })
      : t("beacon:battery-title", {
          count: days,
          days,
          voltage: voltage?.toFixed(2).toLocaleString(),
        });

  return (
    <span title={tooltip} style={{ position: "relative", width: 37 }}>
      <span
        className={
          absolute ? "battery-container-absolute" : "battery-container-relative"
        }
      >
        <span className="battery-bump"></span>
        <span className="battery-outer">
          <span
            className={
              "battery-level" +
              (percentage < 0.1 ? " alert" : percentage < 0.3 ? " warn" : "")
            }
            style={{ height: roundedPercentage + "%" }}
          ></span>
        </span>
      </span>
      {!props.iconOnly && (
        <span
          style={{
            fontSize: 12,
            position: "absolute",
            top: 1,
            left: absolute ? 14 : 30,
          }}
        >
          {roundedPercentage}%
        </span>
      )}
    </span>
  );
}

export function BeaconStatus(props: {
  beacon: Beacon;
  hideErrorStatus?: boolean;
}) {
  const { t } = useTranslation();

  const { beacon, hideErrorStatus } = props;

  const hasWarningsOrErrors = !!beacon?.alerts?.length;
  return (
    <div
      style={{
        marginLeft: 3,
        display: "grid",
        gridTemplateColumns: "auto auto auto 1fr",
      }}
    >
      {beacon.isOn === null ? (
        <Tag color="gray">{t("beacon:is-on-off-unknown")}</Tag>
      ) : beacon.isOn ? (
        <Tag
          color="green"
          style={{ width: 40, textAlign: "center" }}
          title={t("beacon:is-on-off-last-reported-at", {
            lastReportedAt: formatLocalizedDateTime(beacon.lastMsgAt),
          })}
        >
          {t("beacon:is-on-off-on")}
        </Tag>
      ) : (
        <Tag color="black" style={{ width: 40, textAlign: "center" }}>
          {t("beacon:is-on-off-off")}
        </Tag>
      )}
      {!!beacon.sensors?.batt && beacon.isOn && (
        <Battery environment={beacon.sensors?.batt} absolute={true} />
      )}
      {!hideErrorStatus && hasWarningsOrErrors && beacon.isOn && (
        <BeaconErrorStatus
          errors={beacon?.alerts}
          style={{ marginLeft: "15px" }}
          showTooltip
        />
      )}
    </div>
  );
}

export function SCBOneDeviceStatus(props: {
  device: SCBOneDevice;
  hideErrorStatus?: boolean;
}) {
  const { t } = useTranslation();

  const { device, hideErrorStatus } = props;

  const hasWarningsOrErrors = !!device?.alerts?.length;
  return (
    <div
      style={{
        marginLeft: 3,
        display: "grid",
        gridTemplateColumns: "auto auto auto 1fr",
      }}
    >
      {device.isOn === null ? (
        <Tag color="gray">{t("beacon:is-on-off-unknown")}</Tag>
      ) : device.isOn ? (
        <Tag
          color="green"
          style={{ width: 40, textAlign: "center" }}
          title={t("beacon:is-on-off-last-reported-at", {
            lastReportedAt: formatLocalizedDateTime(device.lastMsgAt),
          })}
        >
          {t("beacon:is-on-off-on")}
        </Tag>
      ) : (
        <Tag color="black" style={{ width: 40, textAlign: "center" }}>
          {t("beacon:is-on-off-off")}
        </Tag>
      )}
      {!hideErrorStatus && hasWarningsOrErrors && device.isOn && (
        <BeaconErrorStatus
          errors={device?.alerts}
          style={{ marginLeft: "15px" }}
          showTooltip
        />
      )}
    </div>
  );
}

export function BeaconStatusProject(props: { beacon: BeaconBase }) {
  const { beacon } = props;

  const hasAlerts = !!beacon?.alerts?.length;
  return (
    <div
      style={{
        marginLeft: 3,
        display: "grid",
        gridTemplateColumns: "auto auto auto 1fr",
      }}
    >
      {!!beacon?.batt && beacon.isOn && <Battery environment={beacon?.batt} />}
      {hasAlerts && beacon.isOn && (
        <BeaconErrorStatus
          errors={beacon?.alerts}
          style={{ marginLeft: "15px" }}
          showTooltip
        />
      )}
    </div>
  );
}
